import { useEffect } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { NoAccessView } from "@/shared/components/NoAccessView";
import useTranslations from "@/shared/lang/useTranslations";
import { AuditEntityHistoryModal } from "./AuditEntityHistoryModal";
import { AuditoriaArchivosModal } from "./AuditoriaArchivosModal";
import { AuditoriaPagination } from "./AuditoriaPagination";
import { PaymentAuditFilters } from "./PaymentAuditFilters";
import { PaymentAuditTable } from "./PaymentAuditTable";
import { usePaymentAuditSection } from "../hooks/usePaymentAuditSection";

interface PaymentAuditSectionProps {
  onControlChange?: (ctrl: { refetch: () => void; isLoading: boolean }) => void;
}

export const PaymentAuditSection = ({ onControlChange }: PaymentAuditSectionProps) => {
  const { t } = useTranslations("audit");

  const {
    filters,
    setFilters,
    page,
    pageSize,
    rows,
    totalItems,
    lastPage,
    canGoPrevious,
    canGoNext,
    from,
    to,
    isLoading,
    error,
    paginationOptions,
    pageNumbers,
    handleApplyFilters,
    handleResetFilters,
    handlePageChange,
    handleItemsPerPageChange,
    handleViewHistory,
    handleCloseHistoryModal,
    historyModalOpen,
    historyEntityId,
    historyEntityType,
    archivosGalleryOpen,
    selectedArchivosRow,
    handleViewArchivos,
    handleCloseArchivosModal,
    refetch,
    noAccess,
  } = usePaymentAuditSection();

  useEffect(() => {
    onControlChange?.({ refetch, isLoading });
  }, [refetch, isLoading, onControlChange]);

  if (noAccess) {
    return <NoAccessView />;
  }

  return (
    <div className="space-y-4">
      <PaymentAuditFilters
        filters={filters}
        setFilters={setFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        isLoading={isLoading}
      />

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <AlertCircle className="h-5 w-5 shrink-0" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {isLoading && rows.length === 0 ? (
        <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-lg p-12 flex flex-col items-center justify-center gap-4">
          <Loader2 className="h-10 w-10 animate-spin text-gray-400 dark:text-gray-500" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t("payments_loading")}
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-lg overflow-hidden">
          <PaymentAuditTable
            rows={rows}
            totalItems={totalItems}
            itemsPerPage={pageSize}
            paginationOptions={paginationOptions}
            onItemsPerPageChange={handleItemsPerPageChange}
            onViewArchivos={handleViewArchivos}
            onViewHistory={handleViewHistory}
            t={t}
          />
          <AuditoriaPagination
            currentPage={page}
            lastPage={lastPage}
            canGoPrevious={canGoPrevious}
            canGoNext={canGoNext}
            isLoading={isLoading}
            pageNumbers={pageNumbers}
            from={from}
            to={to}
            total={totalItems}
            onPageChange={handlePageChange}
            recordsText={t("pagination_records", { from, to, total: totalItems })}
            totalPagesText={
              lastPage != null
                ? t("pagination_total_pages", { lastPage })
                : undefined
            }
            firstPageTitle={t("pagination_first_page")}
            prevPageTitle={t("pagination_prev_page")}
            nextPageTitle={t("pagination_next_page")}
            lastPageTitle={t("pagination_last_page")}
          />
        </div>
      )}

      <AuditEntityHistoryModal
        isOpen={historyModalOpen}
        onClose={handleCloseHistoryModal}
        entityType={historyEntityType}
        entityId={historyEntityId}
        filterFlujo="PAYMENT"
      />

      <AuditoriaArchivosModal
        isOpen={archivosGalleryOpen}
        onClose={handleCloseArchivosModal}
        row={selectedArchivosRow}
        alt={t("table_tooltip_view_archivos")}
      />
    </div>
  );
};
